import React from 'react';

import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import IncidentIcon from '@atlaskit/icon/core/incident';
import ProjectIcon from '@atlaskit/icon/core/project';
import PlanIcon from '@atlaskit/icon/core/roadmap';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import Image from '@atlaskit/image';
import GenericTile from '@atlaskit/tile';
import { type SearchResultType } from '@atlassian/search-client';

import { ProductKeys } from '../../constants/products';
import { ProductIcon } from '../product-icon';

type SizeType = 'small' | 'medium';

/**
 * Returns the appropriate icon for a Jira item based on the provided parameters.
 *
 * @param {string | null | undefined} iconUrl - The URL of the icon image.
 * @param {SearchResultType | string} type - The type of the search result (e.g., 'board', 'plan', etc.).
 * @param {boolean} isIssue - Flag indicating if the item is an issue.
 * @param {'small' | 'medium'} [size='small'] - The size of the icon ('small' or 'medium').
 * @param {ProductKeys} [product] - The product key associated with the item.
 * @returns {React.JSX.Element} The JSX element representing the icon.
 */

export const getJiraItemIcon = ({
	iconUrl,
	type,
	isIssue,
	size = 'small',
}: {
	iconUrl: string | null | undefined;
	type: SearchResultType | string;
	isIssue: boolean;
	size?: SizeType;
}): React.JSX.Element => {
	const imageSize = size === 'medium' ? 22 : 16;

	// Use the iconUrl if available
	if (iconUrl && iconUrl !== '') {
		if (isIssue) {
			// wrapper is only needed for issues. Other types don't need the background and padding with iconUrl.
			return (
				<GenericTile label="Work item icon" size={size}>
					<Image testId="jira-entity-icon" width={imageSize} height={imageSize} src={iconUrl} />
				</GenericTile>
			);
		}

		return (
			// The image covers up the whole wrapper
			<GenericTile isInset={false} label={`${type} icon`} size={size}>
				<Image testId="jira-entity-icon" src={iconUrl} />
			</GenericTile>
		);
	}

	if (isIssue) {
		return (
			<GenericTile label="Work item icon" size={size}>
				<WorkItemIcon spacing="none" size={size} label="work-item-icon" />
			</GenericTile>
		);
	}

	let icon: React.JSX.Element;
	switch (type) {
		case 'board':
			icon = <BoardIcon spacing="none" size={size} label="board-icon" />;
			break;
		case 'plan':
			icon = <PlanIcon spacing="none" size={size} label="plan-icon" />;
			break;
		case 'filter':
			icon = <FilterIcon spacing="none" size={size} label="filter-icon" />;
			break;
		case 'dashboard':
			icon = <DashboardIcon spacing="none" size={size} label="dashboard-icon" />;
			break;
		case 'project':
			icon = <ProjectIcon spacing="none" size={size} label="project-icon" />;
			break;
		case 'incident':
			icon = <IncidentIcon spacing="none" size={size} label="incident-icon" />;
			break;

		default:
			// This never happens
			return <ProductIcon size={size} product={ProductKeys.Jira} />;
	}

	return (
		<GenericTile label={`${type} icon`} size={size}>
			{icon}
		</GenericTile>
	);
};
